<template>
  <b-navbar
    class="header"
    toggleable="md"
    type="dark"
    fixed="top"
  >
    <b-navbar-brand
      id="logo" :to="groupRoutePath('/')"
      class="m-0 p-0 mr-4"
    >
      <img
        class="m-3"
        src="@/assets/logo.png"
        alt="Webmag Logo"
      />
    </b-navbar-brand>

    <!-- Breadcrumb lives OUTSIDE the collapse so the page title stays
         visible next to the logo on phones (the collapse only holds the
         action buttons/menu there). On md+ the collapse renders inline, so
         desktop looks the same as before. -->
    <header-breadcrumb
      v-if="userObject && currentPageType"
      class="header-breadcrumb"
      :user-object="userObject"
      :all-item-path-of-teams-items="allItemPathOfTeamsItems"
      :current-item="currentItemObject"
      :group-id="groupId"
      :current-page-type="currentPageType"
      :team-access-to-root-folder="teamAccessToRootFolder"
      :compact="v3Page"
    />

    <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

    <b-collapse id="nav-collapse" is-nav class="">
      <!-- Right aligned nav items -->
      <b-navbar-nav class="ml-auto">
        <header-action-buttons
          class="d-flex align-items-center"
          v-if="showHeaderActionButtons && groupId"
          :user-object="userObject"
          :group-id="groupId"
          :privileges-for-item="(currentItemObject && currentItemObject.privilegesForItem.length)
              ? currentItemObject.privilegesForItem : []"
          :current-page-type="currentPageType"
        />
        <modal-feedback
          v-if="userObject"
          :user-object="userObject"
        />
        <!-- far right, just left of the menu divider; the button itself
             only shows for groups with the v3 editor (enable_vue3_editor) -->
        <header-news-button
          v-if="userObject && groupId"
          class="d-flex align-items-center"
          :user-object="userObject"
          :group-id="groupId"
          :hide-bell="v3Page"
        />
        <!-- A v3 page: account, news and help sit in the bar's "More" menu (founder 17.10.2026). -->
        <header-navigation-right
          v-if="userObject && groupId && !v3Page"
          :group-id="groupId"
          :privileges="currentGroupPrivileges"
          :group-management-privileges=
              "(userObject.groupIdsOfUser ? userObject.groupIdsOfUser.length !== 0 : false)"
          :is-sys-admin="userObject.isSysAdmin"
        />
      </b-navbar-nav>
    </b-collapse>
  </b-navbar>
</template>

<script>
import GetIdBySlugPath from '@/graphQlQueries/queries/getIdBySlugPath';
import UserDataAndAccess from '@/mixins/userDataAndAccess';
import groupNavigation from '@/mixins/groupNavigation';
import { rememberedV3, rememberV3 } from '@/utils/editorKindMemory';

export default {
  name: 'Header',
  mixins: [UserDataAndAccess, groupNavigation],
  components: {
    HeaderActionButtons: () => import('@/components/header/Buttons/HeaderActionButtons.vue'),
    HeaderBreadcrumb: () => import('@/components/header/HeaderBreadcrumb.vue'),
    HeaderNavigationRight: () => import('@/components/header/HeaderNavigationRight.vue'),
    ModalFeedback: () => import('@/components/modals/ModalFeedback.vue'),
    HeaderNewsButton: () => import('@/components/header/Buttons/HeaderNewsButton.vue'),
  },
  data() {
    return {
      currentItemObject: null,
      allItemPathOfTeamsItems: null,
      teamAccessToRootFolder: null,
      // The open editor is a v3 one (KEditor announces it with keditor-preview-mode); until then as last time here.
      v3Editor: rememberedV3(window.location.pathname),
    };
  },
  created() {
    this.usedInHeader = true;
  },
  mounted() {
    this.$root.$on('keditor-preview-mode', this.setV3Editor);
    this.$root.$emit('keditor-preview-frage');
  },
  beforeDestroy() {
    this.$root.$off('keditor-preview-mode', this.setV3Editor);
    document.documentElement.classList.remove('wm-header-compact');
  },
  watch: {
    // The lower bar and every offset under it follow one variable (scss/_global.scss --wm-header-height).
    v3Page: {
      immediate: true,
      handler(on) { document.documentElement.classList.toggle('wm-header-compact', on); },
    },
    userObject(val) {
      this.allItemPathOfTeamsItems = this.getAllTeamsItemPathOfAllItemsInAGroup(
        val.teamItemsOfUser,
      );
    },
  },
  computed: {
    v3Page() {
      return this.currentPageType === 'page' && this.v3Editor;
    },
    showHeaderActionButtons() {
      if (this.currentPageType === 'folder' && !this.isGroupUser) {
        return true;
      }
      return !!(this.userObject && this.currentItemObject && this.currentPageType);
    },
    currentPageType() {
      let pageType = null;
      // remove a potential backslash at the end
      let pathOfPage = this.$route.path;
      if (this.$route.path.slice(-1) === '/') {
        pathOfPage = this.$route.path.substring(0, this.$route.path.length - 1);
      }
      if (this.currentItemObject) {
        if (this.currentItemObject.folder_id) {
          pageType = 'folder';
          if (pathOfPage.endsWith('folder-settings')) {
            pageType = 'folder-settings';
          }
        }
        if (this.currentItemObject.project_id) {
          pageType = 'project';
          if (pathOfPage.endsWith('project-settings')) {
            pageType = 'project-settings';
          }
        }
        if (this.currentItemObject.page_id) {
          pageType = 'page';
          if (pathOfPage.endsWith('page-settings')) {
            pageType = 'page-settings';
          }
        }
      }
      if (pathOfPage === '/settings') {
        pageType = 'general-settings';
      }
      if (pathOfPage === '/account-management') {
        pageType = 'group-settings';
      }
      if (pathOfPage === '/team-management') {
        pageType = 'team-settings';
      }
      if (pathOfPage === '/personalization-management') {
        pageType = 'personalization-settings';
      }
      if (pathOfPage === '/reports') {
        pageType = 'reports-settings';
      }
      if (pathOfPage === '/form-management') {
        pageType = 'form-settings';
      }
      if (pathOfPage === '/media-library') {
        pageType = 'media-library';
      }
      if (this.$route.path === '/') {
        pageType = 'folder';
      }
      return pageType;
    },
  },
  apollo: {
    lookupItemQuery: {
      query: GetIdBySlugPath,
      variables() {
        const path = (this.$route.params.parametersOfUrl)
          ? `/${this.$route.params.parametersOfUrl}`
          : '/';
        return {
          groupId: this.groupId,
          path,
        };
      },
      update(data) {
        console.log('lookupItemQuery', data);
        if (data.item_lookup.length === 0) {
          return null;
        }
        const item = data.item_lookup[0];
        if (typeof item.breadcrumbs === 'string') {
          item.breadcrumbs = JSON.parse(item.breadcrumbs);
        }
        const breadcrumbs = this.prepareTheItemsForTheBreadcrumb(item.breadcrumbs);
        const { userGroupRoleIds } = this.userObject;
        const itemTeamRoles = this.getTheTeamRolesForItem(item.item_path);
        const itemAllRoleIds = [...new Set([
          ...userGroupRoleIds,
          ...itemTeamRoles,
        ])];
        const privilegesForItem = this.getAllPrivilegesByRoles(itemAllRoleIds);
        this.currentItemObject = {
          folder_id: item.folder_id,
          project_id: item.project_id,
          page_id: item.page_id,
          itemPath: item.item_path,
          privilegesForItem,
          breadcrumbs,
        };
        return data;
      },
      skip() {
        return !this.groupId || !this.userObject;
      },
      fetchPolicy: 'network-only',
    },
  },
  methods: {
    setV3Editor(mode, v3) {
      this.v3Editor = v3 === true;
      rememberV3(this.$route.path, this.v3Editor);
    },
    getTheTeamRolesForItem(itemPath) {
      const teamRoles = [];
      this.userObject.teamItemsOfUser.forEach((teamObject) => {
        teamObject.team.teams_items.some((teamItem) => {
          let teamItemItemPath = null;
          if (teamItem.folder) {
            teamItemItemPath = teamItem.folder.item_path;
          }
          if (teamItem.project) {
            teamItemItemPath = teamItem.project.item_path;
          }
          if (teamItem.page) {
            teamItemItemPath = teamItem.page.item_path;
          }
          if (itemPath.search(teamItemItemPath) !== -1 && !teamRoles.includes(teamObject.role_id)) {
            teamRoles.push(teamObject.role_id);
            return true;
          }
          return false;
        });
      });
      return teamRoles;
    },
    getAllTeamsItemPathOfAllItemsInAGroup(users_roles_teams) {
      const itemPathOfTeamItems = [];
      users_roles_teams.forEach((teamObject) => {
        teamObject.team.teams_items.forEach((teamItem) => {
          let itemPathOfItem = null;
          if (teamItem.folder && teamItem.folder.group_id === this.groupId) {
            itemPathOfItem = teamItem.folder.item_path;
            if (teamItem.folder.root === true) {
              this.teamAccessToRootFolder = true;
            }
          }
          if (teamItem.project && teamItem.project.group_id === this.groupId) {
            itemPathOfItem = teamItem.project.item_path;
          }
          if (teamItem.page && teamItem.page.group_id === this.groupId) {
            itemPathOfItem = teamItem.pages.item_path;
          }
          if (itemPathOfItem !== null && !itemPathOfTeamItems.includes(itemPathOfItem)) {
            itemPathOfTeamItems.push(itemPathOfItem);
          }
        });
      });
      return itemPathOfTeamItems;
    },
    prepareTheItemsForTheBreadcrumb(breadcrumbsObject) {
      // add the disable item for all breadcrumb items
      breadcrumbsObject.forEach((breadcrumb, index) => {
        if (breadcrumb.type === 'folder') {
          // check with all teams if the element is inside the path
          if (this.allItemPathOfTeamsItems) {
            // eslint-disable-next-line no-param-reassign
            breadcrumbsObject[index].disabled = true;
            if (this.userObject.isGroupUser || this.userObject.isSysAdmin) {
              // eslint-disable-next-line no-param-reassign
              breadcrumbsObject[index].disabled = false;
            } else {
              this.allItemPathOfTeamsItems.some((teamsItemId) => {
                if (breadcrumb.item_path.search(teamsItemId) !== -1) {
                  // eslint-disable-next-line no-param-reassign
                  breadcrumbsObject[index].disabled = false;
                  return true;
                }
                return false;
              });
            }
          }
        } else {
          // eslint-disable-next-line no-param-reassign
          breadcrumbsObject[index].disabled = false;
        }
      });
      if (!this.$route.params.parametersOfUrl) {
        return null;
      }

      const urlSplitArray = this.$route.params.parametersOfUrl.split('/');
      const itemsForBreadcrumb = [];

      for (let i = 0; i < urlSplitArray.length; i++) {
        const to = (i !== 0)
          ? `${itemsForBreadcrumb[i - 1].to}/${urlSplitArray[i]}`
          : this.groupRoutePath(`/${urlSplitArray[i]}`);
        itemsForBreadcrumb.push({
          text: breadcrumbsObject[i + 1].name,
          to,
          active: false,
          type: breadcrumbsObject[i + 1].type,
          disabled: breadcrumbsObject[i + 1].disabled,
          itemPath: breadcrumbsObject[i + 1].item_path,
        });
      }

      // replace the last element with active == true
      if (itemsForBreadcrumb
        && itemsForBreadcrumb.length
        && this.currentPageType !== null
        && this.currentPageType.includes('settings') === false) {
        itemsForBreadcrumb[itemsForBreadcrumb.length - 1].active = true;
      }
      return itemsForBreadcrumb;
    },
  },
};
</script>

<style scoped lang="scss">
img {
  width: 33px;
  display: block;
}

// Compact bar on a v3 page (founder 17.10.2026): the logo fits the lower bar.
:root.wm-header-compact img {
  width: 26px;
  margin: 11px 14px !important;
}

.header {
  background-color: $site-background-color;
  border-bottom: 1px solid $header-border-color;
}

.navbar {
  padding: 0;

  .header-breadcrumb {
    min-width: 0;
  }

  .navbar-brand {
    background-color: $bg-header-dark;
  }

  .navbar-nav {
    height: var(--wm-header-height, 65px);
  }
}

//@media (min-width: 992px) {
::v-deep  .dropdown-toggle::after {
  display: none !important;
}

//}
</style>
